import { api_configuration } from "../../../common";
import gui_definition_types = api_configuration.libraries.gui_definition_lib.types;
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import DestructorTree from "../../DestructorTree";
import { addMessage } from "../../front/messages";
import { createIdPathBuilderFromNode } from "../../navigation/createIdPathFromNode";
import { hasCollectionCreationPermission } from "../../state 2 interface/permissions";
import * as requery from "../../state 2 interface/reQuery";
import { subscribeOnSave, unSubscribeOnSave } from "./save";
import { iReference } from "../../state/Reference";
import openViewOnNode from "../../front/openViewOnNode";
import * as Status from "../../Status";
import { registerGroup, registerStategroup } from "../../state/registerClientState";
import { iCollection } from "../../state/Collection";
import { iState } from "../../state/Stategroup";
import { iText } from "../../state/Text";

function onViewClose(open_view: api_cs.Copen_views, onClose: () => void) {
	const onRemove = open_view.parent.properties.open_views.subscribe({
		on_remove: (view_key) => {
			if (view_key === open_view.key) {
				onRemove.unsubscribe();
				/* a timeout so that the save action can execute. When it takes longer than
				 * 150 ms. the user can always update the candidates manually. */
				setTimeout(onClose, 150);
			}
		},
	});
}

export function query_entry(
	query: api_cs.Cyes__can_create_entry__yes,
	view: gui_definition_types.views.alan_api.Cviews__,
	window: gui_definition_types.settings.alan_api.Cwindows,
	key?: string
): void {
	query.properties.in_.switch({
		query: function (in_query) {
			const parent_node = in_query.inferences.query().inferences.node();
			const new_node = addCollectionEntry(in_query.properties.collection.ref, key).properties.node;
			const open_view = openViewOnNode(new_node, view, window);
			parent_node.properties.location.switch({
				local: function () {
					onViewClose(open_view, () => {
						requery.execute(query.parent.parent);
					});
				},
				remote: undefined,
			});
		},
		candidates: function ($) {
			entryInCandidateSelection($.inferences.candidates_query(), view, window);
		},
	});
}

export enum DefaultsAction {
	DoNotApply,
	ApplyDefaults,
}

export function entry(
	collection: api_cs.Ccollection,
	view: gui_definition_types.views.alan_api.Cviews__,
	window: gui_definition_types.settings.alan_api.Cwindows
): api_cs.Cnode {
	const new_node = addCollectionEntry(collection).properties.node;
	openViewOnNode(new_node, view, window);
	return new_node;
}

type CsObject = api_cs.Cnode | api_cs.Cstate_group__type__property;
function selectStateOfReferenceTail(tail: api_app.Cobject_path_tail, object: CsObject, destructor: DestructorTree) {
	tail.properties.has_steps.switch({
		no: undefined,
		yes: ($) => {
			const next_tail = $.properties.tail;
			$.properties.type.switch({
				parent: undefined,
				dependency: undefined,
				"reference set": undefined,
				"property value": ($) => {
					const $type = $.properties.property.properties.property.ref.properties.type.state;
					switch ($type.name) {
						case "text":
							throw new Error("unexpected state");
						case "number":
							throw new Error("unexpected state");
						case "file":
							throw new Error("unexpected state");
						case "group":
							selectStateOfReferenceTail(
								next_tail,
								registerGroup($type.node, object as api_cs.Cnode, destructor).properties.node,
								destructor
							);
							break;
						case "state group":
							selectStateOfReferenceTail(
								next_tail,
								registerStategroup($type.node, object as api_cs.Cnode, destructor),
								destructor
							);
							break;
						case "collection":
							throw new Error("unexpected state");
						default:
							unexpectedState($type);
					}
				},
				state: ($) => {
					const state_group = object as api_cs.Cstate_group__type__property;
					const state = state_group.properties.states.get($.properties.state.entry)!;
					iState(state).select("user");
					selectStateOfReferenceTail(next_tail, state.properties.node, destructor);
				},
			});
		},
	});
}

async function entryInCandidateSelection(
	candidate_selection: api_cs.Ccandidates__use_for__query,
	view: gui_definition_types.views.alan_api.Cviews__,
	window: gui_definition_types.settings.alan_api.Cwindows
) {
	const destructor = new DestructorTree();
	const collection = iReference(candidate_selection.inferences.reference_property().parent).collectionContext.value;
	if (!Status.isResolved(collection)) {
		addMessage("warning", "Cannot add entry", { body: "The reference is not resolved. Try again later" });
		return;
	}

	const collection_entry = iCollection(collection).addEntry("user");
	const new_node = collection_entry.properties.node;
	// set reference text value as key of new node
	const reference = candidate_selection.inferences.reference_property().parent.parent;
	const key_of_new_node = collection_entry.properties.key.ref;

	// set reference value as key of new node, but only if the reference is not resolved so the entry does not already exist
	if (
		reference.properties.backend_known.switch({
			no: true,
			yes: reference.properties.navigable.switch({
				no: false,
				yes: ($) =>
					$.properties.resolve_status.properties.status.switch({
						unresolved: true,
						"failed to resolve": true,
						"is busy resolving": false,
						resolved: false,
					}),
			}),
		})
	) {
		reference.properties.is_set.switch({
			no: undefined,
			yes: ($) => {
				iText(key_of_new_node).update($.properties.text, "user");
			},
		});
	}
	selectStateOfReferenceTail(
		candidate_selection.inferences.reference_property().parent.inferences.is_navigable().properties.expression
			.properties.tail.properties.path,
		new_node,
		destructor
	);
	const on_save = subscribeOnSave(($) => {
		if (reference.destroyed) return;
		if ($ === new_node) {
			key_of_new_node.properties.is_set.switch({
				no: undefined,
				yes: ($) => {
					// set key of new node as reference text
					iText(reference).update($.properties.text, "user");
				},
			});
		}
	});
	const open_view = openViewOnNode(new_node, view, window);
	onViewClose(open_view, () => {
		unSubscribeOnSave(on_save);
		destructor?.destroy();
	});
}

export function entryFromEntity(
	node: api_cs.Cnode,
	view: gui_definition_types.views.alan_api.Cviews__,
	window: gui_definition_types.settings.alan_api.Cwindows
): void {
	node.input.entity().switch({
		root: () => {
			addMessage("warning", "Add entry not allowed", { body: "Can not duplicate the root." });
		},
		entry: (collection_entry) => {
			const collection = collection_entry.parent;
			const collection_name = collection.parent.parent.key.entry;
			const id_path = createIdPathBuilderFromNode(collection.parent.parent.parent);
			hasCollectionCreationPermission(id_path, collection_name, (has_create_permission) => {
				if (collection.destroyed) {
					return;
				}
				if (has_create_permission) {
					entry(collection, view, window);
				} else {
					addMessage("warning", "Add entry not allowed", {
						body: "You do not have the sufficient rights to perform this operation.",
					});
				}
			});
		},
	});
}

export function addCollectionEntry(collection: api_cs.Ccollection, key?: string): api_cs.Centries__collection {
	const new_entry = iCollection(collection).addEntry("user", key);
	collection.properties.last_created_entry.set("entry", {
		entry: new_entry.key_value,
	});
	return new_entry;
}
